import { on, ThemeEvents } from '@theme/events'

export const AgenticConfig = {
  cartDrawerTag: 'ui-drawer-cart',
  cartItemsSource: 'ui-cart-items',
  cartRewardsSource: 'cart-rewards',
  productFormSource: 'ui-product-form',
  standardEventsModule: '@theme/standard-events',
  variantGidPrefix: 'gid://shopify/ProductVariant/',
  viewEventTag: 's-view-event'
}

function logBridgeError(scope, error) {
  if (window.Shopify?.designMode || window.__THEME_DEV__) {
    // biome-ignore lint/suspicious/noConsole: dev-only bridge diagnostics
    console.warn(`[agentic] ${scope} error:`, error)
  }
}

function configureOpenCart() {
  const action = window.Shopify?.actions?.openCart
  if (typeof action?.configure !== 'function') return

  action.configure({
    handler() {
      const drawer = document.querySelector(AgenticConfig.cartDrawerTag)
      if (typeof drawer?.open !== 'function') return false
      drawer.open()
      return true
    }
  })
}

function toAddedLines(resource) {
  const items = resource?.items ?? (resource?.id ? [resource] : [])
  return items
    .filter((item) => item?.variant_id ?? item?.id)
    .map((item) => ({
      merchandiseId: `${AgenticConfig.variantGidPrefix}${item.variant_id ?? item.id}`,
      quantity: item.quantity ?? 1
    }))
}

function resolvedCartPromise(CartLinesUpdateEvent, resource) {
  if (typeof CartLinesUpdateEvent.createCartFromAjaxResponse !== 'function') {
    return Promise.resolve({ cart: null })
  }
  return Promise.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(resource)
  })
}

function fetchedCartPromise(CartLinesUpdateEvent) {
  if (typeof CartLinesUpdateEvent.createCartFromAjaxResponse !== 'function') {
    return Promise.resolve({ cart: null })
  }
  const root = window.Shopify?.routes?.root ?? '/'
  return fetch(`${root}cart.js`)
    .then((response) => response.json())
    .then((cart) => ({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart)
    }))
    .catch(() => ({ cart: null }))
}

function dispatchCartAdd(CartLinesUpdateEvent, event, context) {
  const lines = toAddedLines(event.detail?.resource)
  if (lines.length === 0) return

  const target = event.target instanceof Element ? event.target : document
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context,
      lines,
      promise: fetchedCartPromise(CartLinesUpdateEvent)
    })
  )
}

function dispatchCartChange(CartLinesUpdateEvent, event, action) {
  const { changedLineKey } = event.detail.data
  if (!changedLineKey) return

  const quantity = action === 'remove' ? 0 : event.detail.data.changedQuantity
  const target = event.target instanceof Element ? event.target : document
  target.dispatchEvent(
    new CartLinesUpdateEvent({
      action,
      context: 'cart',
      lines: [{ id: changedLineKey, quantity }],
      promise: resolvedCartPromise(CartLinesUpdateEvent, event.detail?.resource)
    })
  )
}

function bridgeCartMutations(CartLinesUpdateEvent) {
  on(ThemeEvents.cartUpdate, (event) => {
    try {
      const { source, changeType } = event.detail?.data ?? {}

      if (source === AgenticConfig.productFormSource) {
        dispatchCartAdd(CartLinesUpdateEvent, event, 'product')
        return
      }

      if (source === AgenticConfig.cartRewardsSource && changeType === 'add') {
        dispatchCartAdd(CartLinesUpdateEvent, event, 'cart')
        return
      }

      if (source === AgenticConfig.cartItemsSource && changeType !== 'add') {
        dispatchCartChange(CartLinesUpdateEvent, event, changeType)
      }
    } catch (error) {
      logBridgeError('cart mutation bridge', error)
    }
  })
}

function productSelectContext(productId) {
  const element = document.querySelector(
    `[data-product-select-context="${productId}"]`
  )
  if (!element?.textContent) return null
  try {
    return JSON.parse(element.textContent)
  } catch {
    return null
  }
}

function variantOptionValues(variant) {
  if (Array.isArray(variant.options)) return variant.options
  return [variant.option1, variant.option2, variant.option3].filter(
    (value) => value != null
  )
}

function toMoney(priceInCents) {
  const currencyCode = window.Shopify?.currency?.active ?? Theme.shop.currency
  return { amount: (Number(priceInCents) / 100).toFixed(2), currencyCode }
}

function buildSelectedOptions(context, variant) {
  const values = variantOptionValues(variant)
  return (context.optionNames ?? [])
    .map((name, index) => ({ name, value: values[index] }))
    .filter((option) => option.value != null)
}

function buildProductSelectPayload(
  ProductSelectEvent,
  context,
  variant,
  productId
) {
  const selectedOptions = buildSelectedOptions(context, variant)
  return new ProductSelectEvent({
    product: {
      handle: context.handle,
      id: productId,
      title: context.title
    },
    promise: Promise.resolve({
      variant: {
        availableForSale: variant.available ?? null,
        id: variant.id,
        price: toMoney(variant.price),
        selectedOptions,
        title: variant.title
      }
    }),
    selectedOptions
  })
}

function bridgeVariantSelect(ProductSelectEvent) {
  on(ThemeEvents.variantUpdate, (event) => {
    try {
      const variant = event.detail?.resource
      if (!variant?.id) return

      const productId =
        event.detail?.data?.newProduct?.id ?? event.detail?.data?.productId
      const context = productSelectContext(productId)
      if (!context) return

      const target = event.target instanceof Element ? event.target : document
      target.dispatchEvent(
        buildProductSelectPayload(
          ProductSelectEvent,
          context,
          variant,
          productId
        )
      )
    } catch (error) {
      logBridgeError('variant select bridge', error)
    }
  })
}

function defineViewEvent(createViewEventElement) {
  if (customElements.get(AgenticConfig.viewEventTag)) return
  customElements.define(AgenticConfig.viewEventTag, createViewEventElement())
}

async function setupStandardEvents() {
  let lib
  try {
    lib = await import(AgenticConfig.standardEventsModule)
  } catch (error) {
    logBridgeError('standard events import', error)
    return
  }

  if (typeof lib.CartLinesUpdateEvent === 'function')
    bridgeCartMutations(lib.CartLinesUpdateEvent)
  if (typeof lib.ProductSelectEvent === 'function')
    bridgeVariantSelect(lib.ProductSelectEvent)
  if (typeof lib.createViewEventElement === 'function')
    defineViewEvent(lib.createViewEventElement)
}

document.addEventListener('DOMContentLoaded', () => {
  configureOpenCart()
  setupStandardEvents()
})
